import api from "../../../api/client";
import React, { useEffect, useState } from "react";
import {
  Card,
  Row,
  Col,
  Typography,
  Tag,
  Button,
  Table,
  Divider,
} from "antd";
import {
  DownloadOutlined,
} from "@ant-design/icons";
import { useFarmProfile } from "../../../contexts/FarmProfileContext";
import moment from "moment";
import { formatKES } from "../../../utils/currency";
import InvoiceHeader from "./InvoiceHeader";
import InvoiceEmailModal from "./InvoiceEmailModal";
import { exportReceipts } from "../../../api";

const { Title, Text } = Typography;

export default function InvoiceView({ saleId }) {
  const [invoice, setInvoice] = useState(null);

  const { profile } = useFarmProfile();
  const [emailOpen, setEmailOpen] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [reminderOpen, setReminderOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    api.get(`/api/sales-invoices/${saleId}`).then((res) => {
      setInvoice(res.data);
    });
  }, [saleId]);

  if (!invoice) return <div>Loading...</div>;

  const viewImage = async (file) => {
    if (!file) return;

    try {
      const response = await api.get(`/uploads/${file?.filename}`, {
        responseType: "blob",
      });

      const url = URL.createObjectURL(response.data);
      window.open(url, "_blank");
    } catch (err) {
      console.error(err);
    }
  };

  const today = new Date();
  const due = invoice.dueDate
    ? new Date(invoice.dueDate)
    : new Date(invoice.recorded);

  const diffDays = Math.floor((today - due) / (1000 * 60 * 60 * 24));

  let ribbon = { text: "UNPAID", color: "#ff4d4f" };

  if (invoice.paymentStatus === "fully_paid") {
    ribbon = { text: "PAID", color: "#52c41a" };
  } else if (diffDays > 0) {
    ribbon = { text: `OVERDUE ${diffDays} DAYS`, color: "#f5222d" };
  } else {
    ribbon = { text: `DUE IN ${Math.abs(diffDays)} DAYS`, color: "#faad14" };
  }

  const formatCategory = (cat) =>
    cat?.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  const qtyMap = [
    { field: "weightKg", label: "kg" },
    { field: "traysCount", label: "trays" },
    { field: "liters", label: "L" },
    { field: "brokenEggsCount", label: "pcs" },
  ];

  const resolveQuantity = (record) => {
    for (let q of qtyMap) {
      if (record[q.field]) {
        return `${record[q.field]} ${q.label}`;
      }
    }

    if (record.quantity) {
      return `${record.quantity} ${record.unit || ""}`;
    }

    return "-";
  };

  const columns = [
    {
      title: "#",
      width: 60,
      render: (_, __, index) => index + 1,
    },
    {
      title: "Item / Description",
      render: (_, record) => {
        const itemName = [
          record.itemType,
          record.category && formatCategory(record.category),
          record.identifier,
        ]
          .filter(Boolean)
          .join(" - ");

        return (
          <div className="text-capitalize">
            <div style={{ fontWeight: 500 }}>
              {itemName || record.productName}
            </div>

            {record.description && (
              <div style={{ fontSize: 12, color: "#888" }}>
                {record.description}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: "Qty",
      align: "right",
      render: (_, record) => <span>{resolveQuantity(record)}</span>,
    },
    {
      title: "Rate",
      dataIndex: "unitPrice",
      align: "right",
      render: (value) =>
        Number(value || 0).toLocaleString(undefined, {
          minimumFractionDigits: 2,
        }),
    },
    {
      title: "Amount",
      dataIndex: "totalPrice",
      align: "right",
      render: (value) => (
        <span style={{ fontWeight: 600 }}>
          {Number(value || 0).toLocaleString(undefined, {
            minimumFractionDigits: 2,
          })}
        </span>
      ),
    },
  ];

  const handleDownloadReceipt = async (record) => {
    await exportReceipts({ id: record.id, format: "pdf" });
  };

  return (
    <>
      <InvoiceHeader
        invoice={invoice}
        refresh={() => {}}
        onSend={() => setEmailOpen(true)}
        onRecordPayment={() => setPaymentOpen(true)}
        onReminder={() => setReminderOpen(true)}
        onShare={() => setShareOpen(true)}
      />
      <Divider />
      <Row gutter={24}>
        {/* LEFT SIDE — PAPER */}
        <Col lg={15} xs={24}>
          <Card
            style={{
              padding: 45,
              minHeight: 800,
            }}
          >
            {/* Ribbon */}
            <div
              style={{
                position: "absolute",
                top: 20,
                right: -30,
                background: ribbon.color,
                color: "#fff",
                padding: "8px 10px",
                transform: "rotate(45deg)",
                fontWeight: 400,
                minWidth: 100,
              }}
            >
              {ribbon.text}
            </div>

            {/* Company + Customer */}
            <Row justify="space-between">
              <Col>
                <Title level={5}>From</Title>
                <Text strong>{profile?.name || "My Company Ltd"}</Text>
                <br />
                <Text>{profile?.address1 || "Nairobi, Kenya"}</Text>
                <br />
                <Text>{profile?.email || "farm@email.com"}</Text>
                <br />
                <Text>{profile?.phone1 || ""}</Text>
              </Col>

              <Col style={{ textAlign: "right" }}>
                <Title>INVOICE</Title>
                <Title level={3} style={{ margin: 0 }}>
                  # {invoice.id}
                </Title>
              </Col>
            </Row>

            {/* <Divider /> */}
            <Row justify="space-between">
              <Col></Col>
              <Col style={{ textAlign: "right" }}>
                <Text strong>Balance Due</Text>
                <Title level={5} style={{ margin: 0 }}>
                  {formatKES(invoice.totalCost - invoice.paidAmount)}
                </Title>
              </Col>
            </Row>

            <Divider />
            <Row gutter={24} align="bottom" style={{ marginBottom: 20 }}>
              {/* Column 1 — Bill To */}
              <Col span={16}>
                <Text level={5}>Bill To</Text>
                <br />
                <Text strong>
                  {invoice.stakeholder?.name || "Unknown Customer"}
                </Text>
              </Col>

              {/* Column 2 & 3 — Invoice Details */}
              <Col span={8}>
                <Row gutter={6}>
                  {/* Row 1 */}
                  <Col
                    span={12}
                    style={{ textAlign: "right", fontWeight: 500 }}
                  >
                    Invoice Date:
                  </Col>
                  <Col span={12} style={{ textAlign: "right" }}>
                    {moment(invoice.recorded).format("DD MMM YYYY")}
                  </Col>
                  <br />
                  <Col
                    span={12}
                    style={{ textAlign: "right", fontWeight: 500 }}
                  >
                    Terms:
                  </Col>
                  <Col span={12} style={{ textAlign: "right" }}>
                    {invoice.terms || "Due on Receipt"}
                  </Col>
                  <br />
                  {/* Row 2 */}
                  <Col
                    span={12}
                    style={{ textAlign: "right", fontWeight: 500 }}
                  >
                    Due Date:
                  </Col>
                  <Col span={12} style={{ textAlign: "right" }}>
                    {invoice.dueDate
                      ? moment(invoice.dueDate).format("DD MMM YYYY")
                      : moment(invoice.recorded).format("DD MMM YYYY")}
                  </Col>
                  <br />
                  {/* Row 3 — Status */}
                  <Col
                    span={12}
                    style={{ textAlign: "right", fontWeight: 500 }}
                  >
                    P.O. #:
                  </Col>
                  <Col span={12} style={{ textAlign: "right" }}>
                    <Tag>{invoice.id}</Tag>
                  </Col>
                </Row>
              </Col>
            </Row>
            <Divider />

            <Table
              size="small"
              columns={columns}
              dataSource={invoice.items}
              pagination={false}
              rowKey="id"
            />

            <Divider />
            <Row gutter={24} align="end" style={{ marginBottom: 20 }}>
              {/* Column 1 — Bill To */}
              <Col span={16}></Col>

              {/* Column 2 & 3 — Invoice Details */}
              <Col
                span={8}
                style={{
                  textAlign: "right",
                  backgroundColor: "#f0f0f0",
                  padding: 12,
                }}
              >
                <Row gutter={6}>
                  {/* Row 1 */}
                  <Col
                    span={12}
                    style={{ textAlign: "right", fontWeight: 500 }}
                  >
                    subtotal:
                  </Col>
                  <Col span={12} style={{ textAlign: "right" }}>
                    {formatKES(invoice.totalCost)}
                  </Col>
                  <br />
                  <Col
                    span={12}
                    style={{ textAlign: "right", fontWeight: 500 }}
                  >
                    Paid:
                  </Col>
                  <Col span={12} style={{ textAlign: "right" }}>
                    {formatKES(invoice.paidAmount)}
                  </Col>
                  <br />
                  {/* Row 2 */}
                  <Col
                    span={12}
                    style={{ textAlign: "right", fontWeight: 500 }}
                  >
                    <Title level={5} style={{ margin: 0 }}>
                      Balance:
                    </Title>
                  </Col>
                  <Col span={12} style={{ textAlign: "right" }}>
                    <Title level={5} style={{ margin: 0 }}>
                      {formatKES(invoice.totalCost - invoice.paidAmount)}
                    </Title>
                  </Col>
                </Row>
              </Col>
            </Row>
            <Row
              gutter={24}
              justify="space-between"
              style={{ marginBottom: 20 }}
            >
              {/* Column 1 — Bill To */}
              <Col span={16}>
                <Title level={5}>Notes:</Title>
                <Text type="secondary">
                  Thank you for your business! Please make payment by the due
                  date.
                </Text>
              </Col>
              <Col span={16}></Col>
            </Row>
          </Card>
        </Col>

        {/* RIGHT SIDE — PAYMENTS PANEL */}
        <Col lg={9} xs={24}>
          <Card title="Payments Received">
            {invoice.payments?.length === 0 && (
              <Text type="secondary">No payments recorded</Text>
            )}

            {invoice.payments.map((p) => (
              <Card key={p.id} size="small" style={{ marginBottom: 12 }}>
                <Row justify="space-between">
                  <Text className="text-capitalize">{p.paymentMethod}</Text>
                  <Text strong>{formatKES(p.amount)}</Text>

                  <Text type="secondary">
                    {moment(p.recorded).format("DD MMM YYYY")}
                  </Text>
                  <Text type="secondary">
                    <Button
                      icon={<DownloadOutlined />}
                      size="small"
                      onClick={() => handleDownloadReceipt(p)}
                    >
                      Receipt
                    </Button>
                  </Text>
                </Row>
              </Card>
            ))}
          </Card>

          <Card title="Extra Data" style={{ marginTop: 10 }}>
            {!invoice.items?.length && (
              <Text type="secondary">No extra data</Text>
            )}

            {invoice.items?.map((item, index) => {
              let parsedExtraData = null;

              try {
                parsedExtraData = item.extraData
                  ? JSON.parse(item.extraData)
                  : null;
              } catch (err) {
                console.error("Invalid extraData JSON", err);
              }

              return (
                <Card key={index} size="small" style={{ marginBottom: 12 }}>
                  <div style={{ marginBottom: 8 }}>
                    <strong>Item:</strong> {item.productName}
                  </div>

                  {parsedExtraData ? (
                    <pre
                      style={{
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                      }}
                    >
                      {JSON.stringify(parsedExtraData, null, 2)}
                    </pre>
                  ) : (
                    <Text type="secondary">No extra data</Text>
                  )}
                </Card>
              );
            })}
          </Card>
          <Card title="Attached Files" style={{ marginTop: 10 }}>
            {!invoice.image && <Text type="secondary">No attached files</Text>}
            {invoice.image &&
              (() => {
                let files = [];

                try {
                  files =
                    typeof invoice.image === "string"
                      ? JSON.parse(invoice.image)
                      : invoice.image;

                  if (!Array.isArray(files)) {
                    files = [files];
                  }
                } catch (e) {
                  console.error("Invalid attachment JSON:", e);
                  return <span>Invalid attachment</span>;
                }

                return (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                    {files.map((file, index) => {
                      const fileName =
                        typeof file === "string" ? file : file.filename;

                      const originalName =
                        typeof file === "object" && file.originalName
                          ? file.originalName
                          : fileName;

                      const fileUrl = `${api.defaults.baseURL}/uploads/${fileName}`;

                      const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(
                        fileName,
                      );

                      return (
                        <div key={index}>
                          {isImage ? (
                            <img
                              src={fileUrl}
                              alt={fileName}
                              style={{
                                width: 120,
                                height: 120,
                                objectFit: "cover",
                                borderRadius: 8,
                                cursor: "pointer",
                                border: "1px solid #ddd",
                              }}
                              onClick={() => viewImage(file)}
                            />
                          ) : (
                            <a
                              href={fileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                display: "inline-block",
                                padding: 10,
                                border: "1px solid #ddd",
                                borderRadius: 8,
                                textDecoration: "none",
                              }}
                            >
                              📄 {originalName}
                            </a>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}

            {/* {invoice.items?.map((item, index) => {
            let parsedExtraData = null;

            try {
              parsedExtraData = item.extraData
                ? JSON.parse(item.extraData)
                : null;
            } catch (err) {
              console.error("Invalid extraData JSON", err);
            }

            return (
              <Card
                key={index}
                size="small"
                style={{ marginBottom: 12 }}
              >
                <div style={{ marginBottom: 8 }}>
                  <strong>Item:</strong> {item.productName}
                </div>

                {parsedExtraData ? (
                  <pre
                    style={{
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                    }}
                  >
                    {JSON.stringify(parsedExtraData, null, 2)}
                  </pre>
                ) : (
                  <Text type="secondary">No extra data</Text>
                )}
              </Card>
            );
          })} */}
          </Card>
        </Col>

        <InvoiceEmailModal
          open={emailOpen}
          onClose={() => setEmailOpen(false)}
          invoice={invoice}
        />
      </Row>
    </>
  );
}
